import type { FC, PropsWithChildren } from "react";
import React, { useCallback, useMemo } from "react";
import { type OAuthError, useAuth0 } from "@auth0/auth0-react";
import cube, { HttpTransport } from "@cubejs-client/core";
import { CubeProvider as CubeClientProvider } from "@cubejs-client/react";
import { MonitoredTransport } from "@/cubeClient/MonitoredTransport";
import logger from "@/helpers/logger";
import { CUBE_POLL_INTERVAL_SECONDS } from "./cubeProvider.constants";
import {
  isAuthRedirectError,
  isCubeConsentRedirectStarted,
  markCubeConsentRedirectStarted,
} from "./cubeProviderConsent";

const apiUrl: string = import.meta.env.VITE_CUBE_API_URL;

/**
 * Wraps a provider for Cube.dev client if the env is supported.
 * Note: must be defined above the lazy routes
 */
const CubeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const fetchCubeToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (err: unknown) {
      const { error_description, error } = err as OAuthError;

      if (isAuthRedirectError(error) && !isCubeConsentRedirectStarted()) {
        markCubeConsentRedirectStarted();
        await loginWithRedirect();
      } else if (!isAuthRedirectError(error)) {
        logger.error(`Cube access error: ${error_description} (${error})`);
      }

      throw err;
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const cubeApi = useMemo(() => {
    const transport = new MonitoredTransport(new HttpTransport({ apiUrl }));

    return cube(fetchCubeToken, {
      apiUrl,
      pollInterval: CUBE_POLL_INTERVAL_SECONDS,
      transport,
    });
  }, [fetchCubeToken]);

  return <CubeClientProvider cubeApi={cubeApi}>{children}</CubeClientProvider>;
};

export default CubeProvider;
